import React, { useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "../services/supabase";
import { loadGoogleMaps } from "../services/loadGoogleMaps";
import useAutoRefresh from "../hooks/useAutoRefresh";


const Dashboard = () => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [zones, setZones] = useState([]);
  const [unassignedRides, setUnassignedRides] = useState([]);
  const [onlineDrivers, setOnlineDrivers] = useState([]);
  const driverMarkersRef = useRef(new Map());
  const [activeRides, setActiveRides] = useState([]);
  const [panicAlerts, setPanicAlerts] = useState([]);

  useAutoRefresh(() => {
  loadOnlineDrivers();
  loadUnassignedRides();
  loadActiveRides();
   loadPanicAlerts();
}, 10000); // ⏱️ toutes les 30 secondes

  // Charger zones depuis Supabase
  const loadZones = async () => {
  const { data, error } = await supabase.rpc("get_zones_geojson");

  if (error) {
    console.error("Erreur zones:", error);
    return;
  }

  const parsedZones = data.map((z) => {
    const geo = JSON.parse(z.geojson);

    // MultiPolygon structure: [ [ [ [lng,lat], ... ] ] ]
    const coords = geo.coordinates[0][0].map(([lng, lat]) => ({
      lat,
      lng,
    }));

    return {
      id: z.id,
      name: z.name,
      coords,
    };
  });

  console.log("📌 ZONES PARSED:", parsedZones);
  setZones(parsedZones);
};

// charger la liste des courses
const loadActiveRides = async () => {
  const now = new Date().toISOString();

  const { data, error } = await supabase
    .from("rides")
    .select(`
      id,
      pickup_address,
      dropoff_address,
      status,
      requested_at,
      date_heure,
      meta,
      driver:drivers!driver_id(num_driver, profiles (
          full_name
        )
)
    `)
    .in("status", ["accepted", "pickup_started", "started"])
    .or(
      `date_heure.is.null,date_heure.gt.${now}`
    )
    .order("requested_at", { ascending: false });

  if (error) {
    console.error("LOAD RIDES ERROR:", error);
  } else {
    setActiveRides(data || []);
  }
};





  // charger la liste des chauffeurs en ligne
  const loadOnlineDrivers = async () => {
  const { data, error } = await supabase
    .from("drivers")
    .select(`
      id,
      num_driver,
      status,
      profiles(full_name, phone),
      vehicles:vehicles!vehicles_driver_id_fkey(brand, model, plate, color, year),
      driver_current_locations (
        lat,
        lng,
        updated_at
      )
    `)
    .in("status", ["online", "busy"])
    .eq("is_active", true)
    .eq("approved", true);

  if (error) {
    console.error("Erreur drivers:", error);
    return;
  }

  setOnlineDrivers(data);
};

const getDriverIcon = (status) => {
  if (status === "online") return "/car_green.png";
  if (status === "busy") return "/car_red.png";
  return "/car_yellow.png";
};

const updateDriverMarkers = useCallback((drivers) => {
  if (!window.google || !mapInstanceRef.current) return;

  const map = mapInstanceRef.current;
  const markersMap = driverMarkersRef.current;

  drivers.forEach((d) => {
    const loc = d.driver_current_locations;
    if (!loc?.lat || !loc?.lng) return;

    const driverId = d.id;
    const position = {
      lat: Number(loc.lat),
      lng: Number(loc.lng),
    };

    const iconUrl = getDriverIcon(d.status);

    // 🔁 MARKER EXISTANT
    if (markersMap.has(driverId)) {
      const marker = markersMap.get(driverId);

      marker.setPosition(position);

      // ✅ update icône si statut change
      const current = marker.getIcon();
      const currentUrl =
        typeof current === "string" ? current : current?.url;

      if (currentUrl !== iconUrl) {
        marker.setIcon({
          url: iconUrl,
          scaledSize: new window.google.maps.Size(32, 32),
        });
      }

      return;
    }

    // 🆕 Nouveau marker
    const marker = new window.google.maps.Marker({
      position,
      map,
      icon: {
        url: iconUrl,
        scaledSize: new window.google.maps.Size(32, 32),
      },
      title: `${d.num_driver} - ${d.profiles?.full_name}`,
    });

    markersMap.set(driverId, marker);
  });

  // 🧹 Supprimer markers des chauffeurs disparus de la liste
  const activeIds = new Set(drivers.map((d) => d.id));

  markersMap.forEach((marker, driverId) => {
    if (!activeIds.has(driverId)) {
      marker.setMap(null);
      markersMap.delete(driverId);
    }
  });
}, []);

//Loader les chauffeurs en panique
const loadPanicAlerts = async () => {
  const { data, error } = await supabase
    .from("panic_alerts")
    .select(`
      id,
      triggered_at,
      drivers (
        id,
        num_driver,
        profiles ( full_name, phone ),
        driver_current_locations (
          lat,
          lng,
          updated_at
        )
      )
    `)
    .eq("is_active", true)
    .order("triggered_at", { ascending: false });

  if (error) {
    console.error("PANIC ALERT ERROR:", error);
    return;
  }

  setPanicAlerts(data || []);
};


// Bouton pour resoudre une alerte
const resolveAlert = async (id) => {
  await supabase
    .from("panic_alerts")
    .update({
      is_active: false,
      resolved_at: new Date().toISOString()
    })
    .eq("id", id);
};





  // Pour les courses non assignés
  const loadUnassignedRides = async () => {
  const { data, error } = await supabase
    .from("rides")
    .select(`
      id,
      pickup_address,
      dropoff_address,
      requested_at,
      date_heure,
      meta
    `)
    .is("driver_id", null)                 // chauffeur non assigné
    .eq("status", "unassigned")            // statut unassigned
    .order("requested_at", { ascending: false });

  if (!error) setUnassignedRides(data);
};

  // Charger Google Maps + zones
  useEffect(() => {
    async function init() {
      try {
        await loadGoogleMaps(process.env.REACT_APP_GOOGLE_MAPS_API_KEY);
        loadZones();
        loadUnassignedRides();
        loadOnlineDrivers();
        loadActiveRides();
      } catch (err) {
        console.error("Erreur:", err);
      }
    }
    init();
  }, []);

  // Dès que Google Maps + zones sont chargés → afficher carte
  useEffect(() => {
    if (!window.google || zones.length === 0) return;

    // Initialiser la carte
    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: 45.5017, lng: -73.5673 },
      zoom: 9,
    });

    mapInstanceRef.current = map;

    // Afficher chaque zone
    zones.forEach((zone) => {
      drawZonePolygon(zone, map);
    });
  }, [zones]);

  // Convertir GeoJSON → Google Maps Polygon
 const drawZonePolygon = (zone, map) => {
  if (!zone.coords || zone.coords.length === 0) return;

  new window.google.maps.Polygon({
    paths: zone.coords,
    map,
    strokeColor: "#e53935",
    strokeOpacity: 0.8,
    strokeWeight: 2,
    fillColor: "#e53935",
    fillOpacity: 0.25,
  });
};




  // 🔥 Realtime drivers + positions
useEffect(() => {
  const channel = supabase
    .channel("drivers-realtime")
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "driver_current_locations",
      },
      (payload) => {
        console.log("Position updated:", payload);
        loadOnlineDrivers(); // 👉 recharge les drivers + markers
      }
    )
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "drivers",
      },
      (payload) => {
        console.log("Driver status update:", payload);
        loadOnlineDrivers(); // 👉 recharge les drivers + markers
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}, []);

useEffect(() => {
  if (!mapInstanceRef.current) return;
  updateDriverMarkers(onlineDrivers);
}, [onlineDrivers, updateDriverMarkers]);


  return (
    <div className="dashboard">
      <div className="dashboard-top">
        
        {/* MAP */}
        <div className="map-container">
          <div ref={mapRef} className="google-map"></div>
        </div>

        {/* PANNEAU DROITE */}
        <div className="right-panel">
          <section className="panel-section">
            <h3>Courses non assignées</h3>
            <div className="rides-list">
                {unassignedRides.length === 0 ? (
                    <p>Aucune course non assignée.</p>
                    ) : (
                        unassignedRides.map((ride) => (
                        <div className="ride-card" key={ride.id}>
                            
                            <div className="ride-header">
                            <span className="ride-id">#{ride.id.slice(0, 6)}</span>
                            <span className="ride-time">
                                {ride.date_heure
                                ? new Date(ride.date_heure).toLocaleString()
                                : new Date(ride.requested_at).toLocaleTimeString()}
                            </span>
                            </div>

                            <div className="ride-body">
                            <p><strong>Client :</strong> {ride.meta.nom}</p>

                            <p className="ride-address">
                                <span className="addr-label">Pickup :</span> {ride.pickup_address}
                            </p>

                            <p className="ride-address">
                                <span className="addr-label">Destination :</span> {ride.dropoff_address}
                            </p>
                            </div>

                            <button
                            className="btn-assign"
                            onClick={() => alert("Rendez-vous à la page des courses pour assigner un chauffeur.")}
                            >
                            Assigner chauffeur
                            </button>
                        </div>
                        ))
                    )}
                    </div>

          </section>

          <section className="panel-section">
            <h3>Chauffeurs en ligne et occupés</h3>
            <div className="drivers-online-list">
                    {onlineDrivers.length === 0 ? (
                        <p>Aucun chauffeur en ligne.</p>
                    ) : (
                        onlineDrivers.map((d) => (
                        <div className="driver-card" key={d.id}>
                            <div className="driver-row">
                            <span className="driver-number">
                                #{d.num_driver?.toLocaleString()}
                            </span>

                            <span className="driver-name">
                                {d.profiles?.full_name}
                            </span>
                            </div>

                            {d.vehicles?.length > 0 ? (
                              <div className="driver-vehicle">
                                {d.vehicles[0]?.brand || "—"}{" "}
                                {d.vehicles[0]?.model || ""}{" "}
                                {d.vehicles[0]?.year || ""}{" "}
                                ({d.vehicles[0]?.plate || "—"})
                              </div>
                            ) : (
                              <div className="driver-vehicle muted">
                                Véhicule non assigné
                              </div>
                            )}
                        </div>
                        ))
                    )}
                    </div>

          </section>

        <section className="panel-section panic-section">
                <h3>Alertes panique</h3>

                {panicAlerts.length === 0 ? (
                    <p>Aucune alerte active.</p>
                ) : (
                    panicAlerts.map((alert) => {
                    const driver = alert.drivers;
                    const loc = driver?.driver_current_locations;

                    return (
                        <div className="panic-card" key={alert.id}>
                        <div className="panic-header">
                            <strong>#{driver?.num_driver}</strong> – {driver?.profiles?.full_name}
                        </div>

                        <div className="panic-body">
                            <p>
                            ⏰{" "}
                            {new Date(alert.triggered_at).toLocaleTimeString()}
                            </p>

                            {loc ? (
                            <p>
                                📍 {Number(loc.lat).toFixed(5)}, {Number(loc.lng).toFixed(5)}
                            </p>
                            ) : (
                            <p>📍 Position inconnue</p>
                            )}
                        </div>

                        <div className="panic-actions">
                            {loc && (
                            <button
                                className="btn-map"
                                onClick={() => {
                                mapInstanceRef.current?.panTo({
                                    lat: Number(loc.lat),
                                    lng: Number(loc.lng),
                                });
                                mapInstanceRef.current?.setZoom(15);
                                }}
                            >
                                Voir sur la carte
                            </button>
                            )}

                            <button
                            className="btn-resolve"
                            onClick={() => resolveAlert(alert.id)}
                            >
                            Résoudre
                            </button>
                        </div>
                        </div>
                    );
                    })
                )}
                </section>

        </div>
      </div>

      {/* BAS */}
      <div className="dashboard-bottom">
        <h3>Courses en cours & à venir</h3>
        <div className="rides-active-list">
  {activeRides.length === 0 ? (
    <p>Aucune course active pour le moment.</p>
  ) : (
    activeRides.map((ride) => (
      <div className="ride-card" key={ride.id}>
        
        <div className="ride-header">
          <span className="ride-id">#{ride.id.slice(0, 6)}</span>

          <span className="ride-status">
            {ride.status === "accepted" && "🟢 Acceptée"}
            {ride.status === "started" && "🟡 En cours"}
            {ride.date_heure && new Date(ride.date_heure) > new Date() && " - À venir"}
          </span>
        </div>

        <div className="ride-body">
          <p>
            <strong>Client :</strong>{" "}
            {ride.meta?.nom || "Client inconnu"}
            </p>
            <p>{ride.meta?.telephone || ""}</p>
          {ride.driver?.profiles?.full_name && (
            <p>
              <strong>Chauffeur :</strong> {ride.driver.profiles.full_name}
            </p>
          )}

          <p className="ride-address">
            <span className="addr-label">Pickup :</span> {ride.pickup_address}
          </p>

          <p className="ride-address">
            <span className="addr-label">Destination :</span> {ride.dropoff_address}
          </p>

          <p className="ride-time">
            {ride.date_heure
              ? "📅 " + new Date(ride.date_heure).toLocaleString()
              : "⏱ " + new Date(ride.requested_at).toLocaleString()}
          </p>
        </div>

      </div>
    ))
  )}
</div>

      </div>
    </div>
  );
};

export default Dashboard;

